import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";

export default function ValidatedVirements({
  apiBase = "https://appapi.unpaidfinancial.com/api",
  statusValue = "valide",   // "valide" | "validé" | "approved"
  txType = "debit",         // optionnel
}) {
  // --- helpers statut
  const normalizeStatus = (s) => {
    const x = String(s || "").toLowerCase();
    if (["valide", "validé", "validated", "approved", "approve"].includes(x)) return "valide";
    if (["en_attente", "pending", "awaiting"].includes(x)) return "en_attente";
    if (["refuse", "refusé", "rejected", "declined"].includes(x)) return "refuse";
    return x || "en_attente";
  };
  const normalizedStatus = useMemo(() => normalizeStatus(statusValue), [statusValue]);

  // --- state
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // filtres
  const [email, setEmail] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  // pagination
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);

  // --- http
  const http = useMemo(() => {
    const instance = axios.create({ baseURL: apiBase });
    instance.interceptors.request.use((cfg) => {
      const token =
        localStorage.getItem("accessToken") || localStorage.getItem("bank_token");
      if (token) cfg.headers.Authorization = `Bearer ${token}`;
      return cfg;
    });
    return instance;
  }, [apiBase]);

  // --- fmt
  const fmtMoney = (n, dev = "EUR") =>
    `${Number(n ?? 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ${dev}`;
  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString(); } catch { return iso ?? ""; }
  };
  const apiError = (e) =>
    e?.response?.data?.error || e?.response?.data?.message || e?.message || "Une erreur est survenue.";

  // --- fetch
  const fetchData = useCallback(
    async (overridePage = null) => {
      if (from && to && from > to) {
        setErr("La date de début doit être antérieure ou égale à la date de fin.");
        return;
      }
      setLoading(true);
      setErr("");
      try {
        // IMPORTANT : endpoint admin + status coté API
        const { data } = await http.get("/bank/transfers", {
          params: {
            status: normalizedStatus,                 // <- impose "valide"
            ...(txType ? { type: txType } : {}),
            sort: "created_at",
            order: "desc",
            per_page: 20,
            page: overridePage ?? page,
            ...(email ? { search: email } : {}),
            ...(from ? { date_from: from } : {}),
            ...(to ? { date_to: to } : {}),
          },
        });

        const apiRows = data?.data ?? [];
        // Filtre de secours côté client si l’API renvoie un mélange
        const onlyWanted = apiRows.filter(
          (r) => normalizeStatus(r?.status) === normalizedStatus
        );

        setRows(onlyWanted);
        setTotal(data?.total ?? onlyWanted.length);
        setLastPage(data?.last_page ?? 1);
      } catch (e) {
        setErr(apiError(e));
      } finally {
        setLoading(false);
      }
    },
    [http, email, from, to, page, normalizedStatus, txType]
  );

  useEffect(() => {
    fetchData();
  }, [page, fetchData]);

  // filtres
  const applyFilters = () => { setPage(1); fetchData(1); };
  const clearFilters = () => {
    setEmail(""); setFrom(""); setTo("");
    setPage(1); fetchData(1);
  };

  // badge couleur
  const StatusBadge = ({ s }) => {
    const st = normalizeStatus(s);
    const map = {
      valide: "bg-emerald-100 text-emerald-800",
      en_attente: "bg-amber-100 text-amber-800",
      refuse: "bg-red-100 text-red-800",
    };
    return (
      <span className={`text-[11px] px-2 py-1 rounded-full font-medium ${map[st] || "bg-gray-100 text-gray-700"}`}>
        {st}
      </span>
    );
  };

  // --- UI
  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      <div className="mb-4 md:mb-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className="text-2xl font-semibold">Virements validés</h1>
            <p className="text-sm text-gray-500">
              Statut : <b>{normalizedStatus}</b> {txType ? <>• Type : <b>{txType}</b></> : null} • Affichage : <b>cartes</b>
            </p>
          </div>
          <button
            onClick={() => fetchData()}
            disabled={loading}
            className="hidden md:inline-flex px-4 h-10 rounded-lg border bg-white hover:bg-gray-50 disabled:opacity-60"
          >
            Actualiser
          </button>
        </div>

        {/* TOOLBAR */}
        <div className="bg-white border rounded-xl p-2">
          <div className="flex flex-wrap items-end gap-2 md:gap-3">
            <Field label="Email (émetteur/bénéficiaire)">
              <input
                placeholder="ex: user@domaine.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                className="w-56 md:w-72 border rounded-lg px-3 h-10 focus:ring-2 focus:ring-black/10"
              />
            </Field>
            <Field label="Depuis">
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="w-40 border rounded-lg px-3 h-10 focus:ring-2 focus:ring-black/10"
              />
            </Field>
            <Field label="Jusqu’au">
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="w-40 border rounded-lg px-3 h-10 focus:ring-2 focus:ring-black/10"
              />
            </Field>

            <div className="ms-auto flex items-center gap-2">
              <button
                onClick={applyFilters}
                disabled={loading}
                className="px-4 h-10 rounded-lg bg-black text-white hover:bg-black/90 disabled:opacity-60"
              >
                Rechercher
              </button>
              <button
                onClick={clearFilters}
                disabled={loading || (!email && !from && !to)}
                className="px-3 h-10 rounded-lg border bg-white hover:bg-gray-50 disabled:opacity-40"
                title="Réinitialiser la recherche"
              >
                Effacer
              </button>
              <button
                onClick={() => fetchData()}
                disabled={loading}
                className="px-4 h-10 rounded-lg border bg-white hover:bg-gray-50 md:hidden disabled:opacity-60"
              >
                Actualiser
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* erreurs */}
      {err && (
        <div className="mb-3 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
          {String(err)}
        </div>
      )}

      {/* skeleton */}
      {loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="animate-pulse p-4 rounded-xl border bg-white">
              <div className="h-3 w-24 bg-gray-200 rounded mb-2" />
              <div className="h-3 w-40 bg-gray-200 rounded mb-2" />
              <div className="h-3 w-32 bg-gray-200 rounded mb-4" />
              <div className="h-10 w-full bg-gray-200 rounded" />
            </div>
          ))}
        </div>
      )}

      {/* empty */}
      {!loading && rows.length === 0 && (
        <div className="p-10 text-center text-gray-500 border rounded-xl bg-white">
          Aucun virement <b>{normalizedStatus}</b>.
        </div>
      )}

      {/* CARTES */}
      {!loading && rows.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map((t) => {
            const emitter = t?.from_account?.user?.email ?? "—";
            const receiver =
              t?.to_account?.user?.email ?? (t?.to_account_id ? "—" : "Externe");
            const dev = t?.from_account?.devise ?? "EUR";
            return (
              <article
                key={t.id}
                className="p-4 rounded-xl border bg-white shadow-sm flex flex-col"
              >
                <div className="flex items-center justify-between mb-2">
                  <StatusBadge s={t.status} />
                  <span className="text-xs text-gray-500">{fmtDate(t.created_at)}</span>
                </div>

                <div className="space-y-1 text-sm">
                  <Row label="Émetteur" value={emitter} />
                  <Row label="Bénéficiaire" value={receiver} />
                  <Row label="Montant" value={fmtMoney(t.amount, dev)} bold />
                  <div className="text-gray-700">
                    {t.description || <span className="text-gray-400">—</span>}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* pagination */}
      {!loading && rows.length > 0 && (
        <div className="mt-4 flex flex-col md:flex-row items-center justify-between gap-3 text-sm">
          <div className="text-gray-600">
            Total : <b>{total}</b> • Page <b>{page}</b> / {lastPage}
          </div>
          <div className="flex items-center gap-2">
            <button
              className="px-4 h-10 rounded-lg border bg-white hover:bg-gray-50 disabled:opacity-40"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Précédent
            </button>
            <button
              className="px-4 h-10 rounded-lg border bg-white hover:bg-gray-50 disabled:opacity-40"
              disabled={page >= lastPage || loading}
              onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
            >
              Suivant
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* —— Sub components —— */
function Field({ label, children }) {
  return (
    <div className="flex flex-col">
      <label className="text-[11px] text-gray-500">{label}</label>
      {children}
    </div>
  );
}
function Row({ label, value, bold }) {
  return (
    <div>
      <span className="text-gray-500">{label}:</span>{" "}
      <span className={bold ? "font-semibold" : ""}>{value}</span>
    </div>
  );
}
